import React, { useEffect, useMemo, useState } from 'react';
import { motion } from 'framer-motion';
import { MapPinIcon } from 'lucide-react';
import type { QuestionRendererProps } from './types';
import { FALLBACK_COLOR } from './rendererUtils';
import { useEdgeTriggeredSubmit } from './useEdgeTriggeredSubmit';
export function CoordinatePlotRenderer({
  question,
  onAnswer,
  onReadyChange,
  shouldValidate,
  moduleColor
}: QuestionRendererProps) {
  const color = moduleColor || FALLBACK_COLOR;
  const data = question.data || {};
  const gridWidth = Number(data.gridWidth ?? 10);
  const gridHeight = Number(data.gridHeight ?? 10);
  const startPoint = data.startPoint as {
    x: number;
    y: number;
    label: string;
  } | undefined;
  const [pickedPoint, setPickedPoint] = useState<{
    x: number;
    y: number;
  } | null>(null);
  useEffect(() => {
    setPickedPoint(null);
  }, [question.id]);
  const isReady = pickedPoint !== null;
  useEffect(() => {
    onReadyChange?.(isReady);
  }, [isReady, onReadyChange]);
  const answerPayload = useMemo(() => {
    return pickedPoint ? JSON.stringify(pickedPoint) : '';
  }, [pickedPoint]);
  useEdgeTriggeredSubmit(answerPayload, shouldValidate, onAnswer, question.id);
  const cellSize = 28;
  const padding = 50;
  const arrowSize = 8;
  const svgWidth = gridWidth * cellSize + padding * 2;
  const svgHeight = gridHeight * cellSize + padding * 2;
  const originX = padding;
  const originY = svgHeight - padding;
  const toSvgX = (x: number) => originX + x * cellSize;
  const toSvgY = (y: number) => originY - y * cellSize;
  const handleGridClick = (e: React.MouseEvent<SVGSVGElement>) => {
    if (shouldValidate) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;
    // Convert SVG coordinates back to grid coordinates
    const gridX = Math.round((x - originX) / cellSize);
    const gridY = Math.round((originY - y) / cellSize);
    if (gridX >= 0 && gridX <= gridWidth && gridY >= 0 && gridY <= gridHeight) {
      setPickedPoint({
        x: gridX,
        y: gridY
      });
    }
  };
  return <div className="flex flex-col items-center gap-3 w-full" data-id="element-10689">
      <div className="text-sm text-stone-500 flex items-center gap-1.5" data-id="element-10690">
        <MapPinIcon className="w-4 h-4" data-id="element-10691" />
        Klik op het assenstelsel om je punt te plaatsen
      </div>

      <div className="bg-slate-50 p-4 rounded-xl border border-slate-100 touch-none" data-id="element-10692">
        <div className="flex justify-center" data-id="element-10693">
          <motion.svg width={svgWidth} height={svgHeight} role="img" aria-label={`Interactief coördinatenstelsel ${gridWidth} bij ${gridHeight}`} onClick={handleGridClick} className={shouldValidate ? 'cursor-default' : 'cursor-pointer'} initial={{
          opacity: 0
        }} animate={{
          opacity: 1
        }} transition={{
          duration: 0.3
        }} data-id="element-10694">
            {/* Grid lines */}
            <g className="grid-lines" data-id="element-10695">
              {Array.from({
              length: gridWidth + 1
            }).map((_, i) => <line key={`v-${i}`} x1={toSvgX(i)} y1={toSvgY(0)} x2={toSvgX(i)} y2={toSvgY(gridHeight)} stroke="#e2e8f0" strokeWidth="1" data-id="element-10696" />)}
              {Array.from({
              length: gridHeight + 1
            }).map((_, i) => <line key={`h-${i}`} x1={toSvgX(0)} y1={toSvgY(i)} x2={toSvgX(gridWidth)} y2={toSvgY(i)} stroke="#e2e8f0" strokeWidth="1" data-id="element-10697" />)}
            </g>

            {/* X-axis */}
            <g className="x-axis" data-id="element-10698">
              <line x1={originX - 10} y1={originY} x2={toSvgX(gridWidth) + 15} y2={originY} stroke="#475569" strokeWidth="2" data-id="element-10699" />
              <polygon points={`${toSvgX(gridWidth) + 15},${originY} ${toSvgX(gridWidth) + 15 - arrowSize},${originY - arrowSize / 2} ${toSvgX(gridWidth) + 15 - arrowSize},${originY + arrowSize / 2}`} fill="#475569" data-id="element-10700" />
              <text x={toSvgX(gridWidth) + 20} y={originY + 5} className="text-sm font-bold fill-slate-600" data-id="element-10701">
                x
              </text>
            </g>

            {/* Y-axis */}
            <g className="y-axis" data-id="element-10702">
              <line x1={originX} y1={originY + 10} x2={originX} y2={toSvgY(gridHeight) - 15} stroke="#475569" strokeWidth="2" data-id="element-10703" />
              <polygon points={`${originX},${toSvgY(gridHeight) - 15} ${originX - arrowSize / 2},${toSvgY(gridHeight) - 15 + arrowSize} ${originX + arrowSize / 2},${toSvgY(gridHeight) - 15 + arrowSize}`} fill="#475569" data-id="element-10704" />
              <text x={originX - 5} y={toSvgY(gridHeight) - 20} textAnchor="middle" className="text-sm font-bold fill-slate-600" data-id="element-10705">
                y
              </text>
            </g>

            {/* X-axis numbers */}
            {Array.from({
            length: gridWidth + 1
          }).map((_, i) => <text key={`x-num-${i}`} x={toSvgX(i)} y={originY + 20} textAnchor="middle" className="text-xs fill-slate-500" data-id="element-10706">
                {i}
              </text>)}

            {/* Y-axis numbers */}
            {Array.from({
            length: gridHeight + 1
          }).map((_, i) => <text key={`y-num-${i}`} x={originX - 15} y={toSvgY(i) + 4} textAnchor="middle" className="text-xs fill-slate-500" data-id="element-10707">
                {i}
              </text>)}

            {/* Start Point */}
            {startPoint && <g data-id="element-10708">
                <circle cx={toSvgX(startPoint.x)} cy={toSvgY(startPoint.y)} r={8} fill="#94a3b8" stroke="white" strokeWidth="2" data-id="element-10709" />
                <text x={toSvgX(startPoint.x) + 12} y={toSvgY(startPoint.y) - 8} className="text-sm font-bold fill-slate-500" data-id="element-10710">
                  {startPoint.label}
                </text>
              </g>}

            {/* Picked Point */}
            {pickedPoint && <motion.g initial={{
            scale: 0
          }} animate={{
            scale: 1
          }} transition={{
            type: 'spring',
            stiffness: 400,
            damping: 25
          }} data-id="element-10711">
                <circle cx={toSvgX(pickedPoint.x)} cy={toSvgY(pickedPoint.y)} r={10} fill={color} stroke="white" strokeWidth="2" data-id="element-10712" />
                <text x={toSvgX(pickedPoint.x) + 14} y={toSvgY(pickedPoint.y) + 4} className="text-sm font-bold" style={{
              fill: color
            }} data-id="element-10713">
                  ({pickedPoint.x}, {pickedPoint.y})
                </text>
              </motion.g>}

            {/* Invisible hit targets for mobile */}
            {!shouldValidate && Array.from({
            length: gridWidth + 1
          }).map((_, x) => Array.from({
            length: gridHeight + 1
          }).map((_, y) => <circle key={`hit-${x}-${y}`} cx={toSvgX(x)} cy={toSvgY(y)} r={14} fill="transparent" className="cursor-pointer" onClick={e => {
            e.stopPropagation();
            setPickedPoint({
              x,
              y
            });
          }} data-id="element-10714" />))}
          </motion.svg>
        </div>
      </div>

      <p className="text-sm text-stone-500 flex items-center gap-1.5 min-h-[1.25rem]" aria-live="polite" data-id="element-10715">
        {pickedPoint ? <>
            <span className="inline-block w-2 h-2 rounded-full" style={{
          backgroundColor: color
        }} aria-hidden="true" data-id="element-10716" />
            Punt geplaatst op ({pickedPoint.x}, {pickedPoint.y})
          </> : <span className="text-stone-400" data-id="element-10717">
            Kies een punt op het assenstelsel
          </span>}
      </p>
    </div>;
}